import { Config } from '@cian/config';
import * as React from 'react';

import { IParam } from '.';
import { ILocation } from '../../../api/locations';
import {
  EContractType,
  EGarageType,
  EHouseMaterial,
  ELandStatusType,
  EPlacementType,
  ESaleType,
  IJsonQuery,
} from '../../../json_query';
import {
  dealTypeFromJsonQuery,
  FDealType,
  FOfferType,
  isCommercial,
  isFlat,
  isSuburban,
  isUrban,
  offerTypeFromJsonQuery,
} from '../../../utils/category';
import { getRegionIds, isGeoAvailable, MoscowId, moscowMoIds } from '../../../utils/geo';
import { getParamTitleHelper } from './helpers';
import { AccessSystem } from './params/access_system';
import { AreaContainer } from './params/area';
import Balconies from './params/balconies';
import BedroomsCount from './params/bedrooms_min_count';
import { BuildingClass } from './params/building_class';
import { WarehouseBuildingClass } from './params/building_class_warehouse';
import { BuildingCondition } from './params/building_condition';
import { BuildingCrane } from './params/building_crane';
import { BuildingElevator } from './params/building_elevator';
import { BuildingGates } from './params/building_gates';
import { BuildingHeating } from './params/building_heating';
import BuildingPurpose from './params/building_purpose';
import BuildingType from './params/building_type';
import CeilingHeight from './params/ceiling_height';
import Comission from './params/comission';
import Contract from './params/contract';
import Decoration from './params/decoration';
import DemolishedInMoscowProgramm from './params/demolished_in_moscow_programm';
import Developer from './params/developer';
import DoneYear from './params/done_year';
import DwellingStatus from './params/dwelling_status';
import Elevators from './params/elevators';
import { Entrance } from './params/entrance';
import EstateType from './params/estate_type';
import Facilities from './params/facilities';
import Floor from './params/floor';
import FloorsTotal from './params/floors_total';
import FromMKAD from './params/from_mkad';
import { GarageKindParam } from './params/garage_kind';
import { GarageMaterialParam } from './params/garage_material';
import { GarageStatusParam } from './params/garage_status';
import GarageTechincalCharacteristics from './params/garage_technical_characteristics';
import { Garage } from './params/garage_type';
import HasEquipment from './params/has_equipment';
import HasFurniture from './params/has_furniture';
import Heating from './params/heating';
import HouseArea from './params/house_area';
import HouseLineBuilding from './params/house_line.building';
import HouseLineShoppingArea from './params/house_line.shopping_area';
import HouseMaterialComponent from './params/house_material';
import HouseYear from './params/house_year';
import { Infrastructure } from './params/infrastructure';
import IsByHomeOwner from './params/is_by_homeowner';
import LandStatus from './params/land_status';
import Layout from './params/layout';
import { ManufactureCondition } from './params/manufacture_condition';
import Metro from './params/metro';
import OfferId from './params/offer_id';
import { OfficeCondition } from './params/office_condition';
import Parking from './params/parking';
import Phone from './params/phone';
import PlacementTypeComponent from './params/placement_type';
import PropertyType from './params/property_type';
import PublishPeriod from './params/publish_period';
import CoworkingPublishPeriod from './params/coworking_publish_period';
import { CoworkingAccess } from './params/coworking_access';
import RentTerms from './params/rent_terms';
import RentTime from './params/rent_time';
import Repair from './params/repair';
import RoomsInDeal from './params/rooms_in_deal';
import RoomsTotal from './params/rooms_total';
import SaleTypeComponent from './params/sale_type';
import SiteArea from './params/site_area';
import SpecialtyTypes from './params/specialty_types';
import SuburbanUtilities from './params/suburban_utilities';
import SuburbanWC from './params/suburban_wc';
import Tenants from './params/tenants';
import Video from './params/video';
import WC from './params/wc';
import Windows from './params/windows';
import WithPhoto from './params/with_photo';
import WordsExclude from './params/words_exclude';
import WordsInclude from './params/words_include';
import { suburbanMaterials } from '../../state/tags/definitions/house_material';
import { isRoomTypeAvailable, isWithLayoutAvailable } from '../../../utils/layout';
import Amenities from './params/amenities';

// tslint:disable:max-file-line-count
export interface IParams {
  // Дома под снос
  demolishedInMoscowProgramm: IParam;
  // Содержит слова
  wordsInclude: IParam;
  // Исключить слова
  wordsExclude: IParam;
  // Контактный телефон
  phone: IParam;
  // Номер объявления
  offerId: IParam;
  // Дата публикации
  publishPeriod: IParam;
  // Год сдачи ЖК
  newbuildingCompletionYear: IParam;
  // Тип объявления
  developer: IParam;
  // Срок аренды
  rentTime: IParam;
  // Комнат в аренду / на продажу
  roomsInDeal: IParam;
  // Планировка
  layout: IParam;
  // Комнат в квартире
  roomsTotal: IParam;
  // Что нужно в квартире
  facilities: IParam;
  // Площадь
  area: IParam;
  // Этаж
  floor: IParam;
  // Этажей в доме/здании
  floorsTotal: IParam;
  // Тип дома
  houseMaterial: IParam;
  // Год постройки
  houseYear: IParam;
  // Высота потолков
  ceilingHeight: IParam;
  // Лифтов минимум
  elevators: IParam;
  // Балкон
  balconies: IParam;
  // Окна
  windows: IParam;
  // Ремонт
  repair: IParam;
  // Санузел
  bathrooms: IParam;
  // Отделка
  decoration: IParam;
  // Статус помещения
  dwellingStatus: IParam;
  // Состав съемщиков
  tenants: IParam;
  // Тип продажи
  saleType: IParam;
  // Парковка
  parking: IParam;
  // До метро
  undergroundTime: IParam;
  // Условия сделки
  rentTerms: IParam;
  // Комиссия
  comission: IParam;
  // Площадь дома
  houseArea: IParam;
  // Площадь участка
  landArea: IParam;
  // Отопление
  heatingType: IParam;
  // Санузел
  suburbanBathroom: IParam;
  // Спален
  bedrooms: IParam;
  // Наличие на участке
  suburbanUtilities: IParam;
  // Статус участка
  landStatus: IParam;
  // Удаленность
  mkadDistance: IParam;
  // Наличие фото
  withPhoto: IParam;
  // Возможное назначение / Сфера деятельности (бизнес) (1/2)
  activityField: IParam;
  // Возможное назначение (здание)
  buildingPurpose: IParam;
  // Тип здания
  buildingType: IParam;
  // Класс (1/2)
  buildingClass: IParam;
  // Класс (2/2)
  buildingClassWarehouse: IParam;
  // Тип помещения
  placementType: IParam;
  // Линия домов (1/2)
  houseLineShoppingArea: IParam;
  // Тип недвижимости
  propertyType: IParam;
  // Недвижимость в
  estateType: IParam;
  // Линия домов (2/2)
  houseLineBuilding: IParam;
  // Вход (офис)
  accessSystem: IParam;
  // Вход (остальные)
  entrance: IParam;
  // Тип договора
  contract: IParam;
  // Состояние (офис)
  officeCondition: IParam;
  // Состояние (2/3)
  manufactureCondition: IParam;
  // Состояние (3/3)
  buildingCondition: IParam;
  // Наличие мебели
  furniture: IParam;
  // Наличие оборудования
  equipment: IParam;
  // Тип
  garageKind: IParam;
  // Тип гаража
  garageType: IParam;
  // Тип бокса
  garageMaterial: IParam;
  // Статус (гаража)
  garageStatus: IParam;
  // Технические характеристики
  garageFacilities: IParam;
  // Лифт
  buildingElevator: IParam;
  // Кран
  buildingCrane: IParam;
  // Ворота
  buildingGates: IParam;
  // Отопление
  buildingHeating: IParam;
  // Инфраструктура
  infrastructure: IParam;
  // От собственника
  isByHomeowner: IParam;
  // Видео
  video: IParam;
  /** Пригород */
  withNeighbors: IParam;
  /** Бизнес-центр */
  bsСenter: IParam;
  /** ЖК */
  geoNewobject: IParam;
  /** Корпус ЖК */
  geoNewbuildingHouse: IParam;
  /** Удобства в coworking'aх */
  amenities: IParam;
  /** Дата публикации для коворкингов*/
  coworkingPublishPeriod: IParam;
  /** Доступ в коворкинг */
  coworkingAccess: IParam;
}

export interface IGetParamsParameters {
  jsonQuery: IJsonQuery;
  currentLocation: ILocation | 'moscow_mo' | 'spb_lo';
  config: Config;
}

export const getParams = ({ jsonQuery, currentLocation, config }: IGetParamsParameters): IParams => {
  const dealType = dealTypeFromJsonQuery(jsonQuery);
  const offerType = offerTypeFromJsonQuery(jsonQuery);

  const commercialOfferTypes = [
    FOfferType.Office,
    FOfferType.TradeArea,
    FOfferType.Warehouse,
    FOfferType.FreeAppointmentObject,
    FOfferType.PublicCatering,
    FOfferType.Garage,
    FOfferType.Manufacture,
    FOfferType.AutoService,
    FOfferType.Business,
    FOfferType.Building,
    FOfferType.DomesticServices,
    FOfferType.CommercialLand,
  ];

  const paramTitleHelper = getParamTitleHelper(commercialOfferTypes, offerType);

  // tslint:disable-next-line:max-line-length
  const neightborsAvailableOfferType =
    (Number(config.get('neighbors.availableOfferType')) as FOfferType) || FOfferType.Any;

  return {
    // Дома под снос
    demolishedInMoscowProgramm: {
      showCondition: {
        dealType: FDealType.Sale | FDealType.RentLongterm,
        offerType: FOfferType.Urban,
        custom:
          (offerType & (FOfferType.Urban & ~FOfferType.FlatNew & ~FOfferType.Bed)) !== 0 &&
          (getRegionIds(currentLocation).some(id => id === MoscowId) ||
            (typeof currentLocation !== 'string' && currentLocation.parentId === MoscowId)),
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, demolished_in_moscow_programm: undefined };
      },
      element: <DemolishedInMoscowProgramm />,
    },

    // Видео
    video: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Any & ~FOfferType.Coworking,
      },
      element: <Video />,
    },

    // Автор объявления
    isByHomeowner: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Residential & ~FOfferType.FlatNew,
      },
      clearJsonQuery: nextJsonQuery => {
        const nextOfferType = offerTypeFromJsonQuery(nextJsonQuery);

        if (isCommercial(nextOfferType) || nextOfferType === FOfferType.FlatNew) {
          return { ...nextJsonQuery, is_by_homeowner: undefined };
        }

        return nextJsonQuery;
      },
      element: <IsByHomeOwner />,
    },

    // Содержит слова
    wordsInclude: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Any,
      },
      element: <WordsInclude />,
    },

    // Исключить слова
    wordsExclude: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Any,
      },
      element: <WordsExclude />,
    },

    // Контактный телефон
    phone: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Any,
      },
      element: <Phone />,
    },

    // Номер объявления
    offerId: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Any,
      },
      element: <OfferId />,
    },

    // Дата публикации
    publishPeriod: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Any,
      },
      element: <PublishPeriod />,
    },

    // Год сдачи ЖК
    newbuildingCompletionYear: {
      showCondition: {
        dealType: FDealType.Sale,
        offerType: FOfferType.FlatNew,
        custom: offerType === FOfferType.FlatNew,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, hand_over: undefined, year: undefined, yeargte: undefined };
      },
      element: <DoneYear />,
    },

    // Тип объявления
    developer: {
      showCondition: {
        dealType: FDealType.Sale,
        offerType: FOfferType.FlatNew,
        custom: offerType === FOfferType.FlatNew,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, from_developer: undefined, promo: undefined };
      },
      element: <Developer currentLocation={currentLocation} />,
    },

    // Срок аренды
    rentTime: {
      showCondition: {
        dealType: FDealType.RentLongterm,
        offerType: FOfferType.Residential,
      },
      element: <RentTime />,
    },

    // Комнат в аренду / на продажу
    roomsInDeal: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Room | FOfferType.Bed,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, rooms_for_sale: undefined };
      },
      element: <RoomsInDeal dealType={dealType} />,
    },

    // Планировка
    layout: {
      showCondition: {
        dealType: FDealType.Sale | FDealType.RentLongterm,
        offerType: FOfferType.Residential & ~FOfferType.Land,
      },
      distinctJsonQuery: jsonQuery => {
        let nextJsonQuery = { ...jsonQuery };

        if (!isRoomTypeAvailable(jsonQuery)) {
          nextJsonQuery = { ...nextJsonQuery, room_type: undefined };
        }

        if (!isWithLayoutAvailable(nextJsonQuery)) {
          nextJsonQuery = { ...nextJsonQuery, with_layout: undefined };
        }

        return nextJsonQuery;
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, with_layout: undefined, room_type: undefined };
      },
      element: <Layout />,
    },

    // Комнат в квартире
    roomsTotal: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Room | FOfferType.FlatShared | FOfferType.Bed,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, rooms_count: undefined };
      },
      element: <RoomsTotal />,
    },

    // Что нужно в квартире
    facilities: {
      showCondition: {
        dealType: FDealType.Rent,
        offerType: FOfferType.Residential & ~FOfferType.Land,
      },
      clearJsonQuery: nextJsonQuery => {
        return {
          ...nextJsonQuery,
          has_furniture: undefined,
          has_fridge: undefined,
          has_washer: undefined,
          has_kitchen_furniture: undefined,
          internet: undefined,
          tv: undefined,
          conditioner: undefined,
          dish_washer: undefined,
          phone: undefined,
          bath: undefined,
          shower: undefined,
          kids: undefined,
          pets: undefined,
          bathhouse: undefined,
          garage: undefined,
          pool: undefined,
          security: undefined,
        };
      },
      element: <Facilities dealType={dealType} offerType={offerType} />,
    },

    // Площадь
    area: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Urban,
      },
      distinctJsonQuery: nextJsonQuery => {
        const nextOfferType = offerTypeFromJsonQuery(nextJsonQuery);

        if ((nextOfferType & (FOfferType.Room | FOfferType.Bed)) === 0) {
          return { ...nextJsonQuery, room_area: undefined };
        }

        if ((nextOfferType & (FOfferType.Flat | FOfferType.FlatShared)) === 0) {
          return { ...nextJsonQuery, living_area: undefined };
        }

        return nextJsonQuery;
      },
      clearJsonQuery: nextJsonQuery => {
        const nextOfferType = offerTypeFromJsonQuery(nextJsonQuery);

        if (nextOfferType & (FOfferType.Land | FOfferType.CommercialLand)) {
          return {
            ...nextJsonQuery,
            total_area: undefined,
            room_area: undefined,
            living_area: undefined,
            kitchen: undefined,
          };
        }

        return { ...nextJsonQuery, room_area: undefined, living_area: undefined, kitchen: undefined };
      },
      element: <AreaContainer offerType={offerType} />,
    },

    // Этаж
    floor: {
      showCondition: {
        dealType: FDealType.Any,
        offerType:
          FOfferType.Urban |
          FOfferType.Office |
          FOfferType.TradeArea |
          FOfferType.Manufacture |
          FOfferType.Warehouse |
          FOfferType.FreeAppointmentObject |
          FOfferType.PublicCatering |
          FOfferType.AutoService |
          FOfferType.DomesticServices |
          FOfferType.Business |
          FOfferType.Coworking,
      },
      distinctJsonQuery: nextJsonQuery => {
        const nextOfferType = offerTypeFromJsonQuery(nextJsonQuery);

        if (!isUrban(nextOfferType)) {
          return { ...nextJsonQuery, is_first_floor: undefined, not_last_floor: undefined };
        } else if (
          (nextOfferType &
            (FOfferType.Office |
              FOfferType.TradeArea |
              FOfferType.Manufacture |
              FOfferType.Warehouse |
              FOfferType.FreeAppointmentObject |
              FOfferType.PublicCatering |
              FOfferType.AutoService |
              FOfferType.DomesticServices)) ===
          0
        ) {
          return {
            ...nextJsonQuery,
            is_basement: undefined,
            is_semibasement: undefined,
            is_first_floor: isUrban(nextOfferType) || isFlat(nextOfferType) ? nextJsonQuery.is_first_floor : undefined,
          };
        }

        return nextJsonQuery;
      },
      clearJsonQuery: nextJsonQuery => {
        return {
          ...nextJsonQuery,
          floor: undefined,
          is_basement: undefined,
          is_semibasement: undefined,
          is_first_floor: undefined,
          not_last_floor: undefined,
        };
      },
      element: <Floor offerType={offerType} />,
    },

    // Этажей в доме/здании
    floorsTotal: {
      showCondition: {
        dealType: FDealType.Any,
        offerType:
          (FOfferType.Residential & ~FOfferType.Land) |
          FOfferType.Office |
          FOfferType.TradeArea |
          FOfferType.Manufacture |
          FOfferType.Warehouse |
          FOfferType.FreeAppointmentObject |
          FOfferType.Building |
          FOfferType.Business |
          FOfferType.Coworking,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, floorn: undefined };
      },
      element: <FloorsTotal offerType={offerType} />,
    },

    // Тип дома
    houseMaterial: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Residential & ~FOfferType.Land,
      },
      distinctJsonQuery: nextJsonQuery => {
        const nextOfferType = offerTypeFromJsonQuery(nextJsonQuery);

        let nextHouseMaterials =
          nextJsonQuery.house_material &&
          nextJsonQuery.house_material.value.filter((type: EHouseMaterial) => {
            if (nextOfferType === FOfferType.FlatNew) {
              return ([
                EHouseMaterial.Panel,
                EHouseMaterial.Brick,
                EHouseMaterial.Monolith,
              ] as EHouseMaterial[]).includes(type);
            } else if ((nextOfferType & FOfferType.Suburban) !== 0) {
              return suburbanMaterials.includes(type);
            }

            return true;
          });

        if (nextHouseMaterials && nextHouseMaterials.length === 0) {
          nextHouseMaterials = undefined;
        }

        return {
          ...nextJsonQuery,
          house_material: nextHouseMaterials
            ? {
                type: 'terms',
                value: nextHouseMaterials,
              }
            : undefined,
        };
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, house_material: undefined };
      },
      element: <HouseMaterialComponent offerType={offerType} />,
    },

    // Год постройки
    houseYear: {
      showCondition: {
        dealType: FDealType.Any,
        offerType:
          (FOfferType.Residential & ~FOfferType.FlatNew & ~FOfferType.Land) |
          (FOfferType.Commercial & ~FOfferType.CommercialLand & ~FOfferType.Business & ~FOfferType.Coworking),
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, house_year: undefined };
      },
      element: <HouseYear offerType={offerType} />,
    },

    // Высота потолков
    ceilingHeight: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Urban | FOfferType.Manufacture | FOfferType.Warehouse,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, ceiling_height: undefined };
      },
      element: <CeilingHeight />,
    },

    // Лифтов минимум
    elevators: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Urban & ~FOfferType.FlatNew,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, lifts: undefined, lift_service: undefined };
      },
      element: <Elevators />,
    },

    // Балкон
    balconies: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Urban & ~FOfferType.FlatNew,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, balconies: undefined, loggia: undefined };
      },
      element: <Balconies />,
    },

    // Окна
    windows: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Urban & ~FOfferType.FlatNew,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, windows_type: undefined };
      },
      element: <Windows />,
    },

    // Ремонт
    repair: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Urban & ~FOfferType.FlatNew & ~FOfferType.Land,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, repair: undefined };
      },
      element: <Repair />,
    },

    // Санузел
    bathrooms: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Urban & ~FOfferType.FlatNew,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, isWCfixed: undefined, wc_type: undefined, wc: undefined };
      },
      element: <WC />,
    },

    // Отделка
    decoration: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.FlatNew,
        custom: offerType === FOfferType.FlatNew,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, has_decoration: undefined };
      },
      element: <Decoration />,
    },

    // Статус помещения
    dwellingStatus: {
      showCondition: {
        dealType: FDealType.Sale | FDealType.RentLongterm,
        offerType: FOfferType.Flat | FOfferType.FlatShared,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, apartment: undefined, only_flat: undefined, penthouse: undefined };
      },
      element: <DwellingStatus />,
    },

    // Состав съемщиков
    tenants: {
      showCondition: {
        dealType: FDealType.Rent,
        offerType: FOfferType.Room,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, composition: undefined };
      },
      element: <Tenants />,
    },

    // Тип продажи
    saleType: {
      showCondition: {
        dealType: FDealType.Sale,
        offerType: FOfferType.Urban,
      },
      distinctJsonQuery: nextJsonQuery => {
        const nextOfferType = offerTypeFromJsonQuery(nextJsonQuery);

        let nextSaleTypes =
          nextJsonQuery.sost_type &&
          nextJsonQuery.sost_type.value.filter(type => {
            if (nextOfferType === FOfferType.FlatNew) {
              return new Array<ESaleType>(ESaleType.F3, ESaleType.GSKContract, ESaleType.Reassignment).includes(type);
            } else {
              return !new Array<ESaleType>(ESaleType.F3, ESaleType.GSKContract, ESaleType.Reassignment).includes(type);
            }
          });

        if (nextSaleTypes && nextSaleTypes.length === 0) {
          nextSaleTypes = undefined;
        }

        return {
          ...nextJsonQuery,
          sost_type: nextSaleTypes
            ? {
                type: 'terms',
                value: nextSaleTypes,
              }
            : undefined,
        };
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, sost_type: undefined, ipoteka: undefined };
      },
      element: <SaleTypeComponent offerType={offerType} />,
    },

    // Парковка
    parking: {
      showCondition: {
        dealType: FDealType.Any,
        offerType:
          (FOfferType.Urban & ~FOfferType.FlatNew) |
          FOfferType.Office |
          FOfferType.Building |
          FOfferType.Garage |
          FOfferType.Business,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, parking_type: undefined };
      },
      element: <Parking />,
    },

    // До метро
    undergroundTime: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Any,
        custom: typeof currentLocation === 'string' || currentLocation.hasMetro,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, only_foot: undefined, foot_min: undefined };
      },
      element: <Metro />,
    },

    // Условия сделки
    rentTerms: {
      showCondition: {
        dealType: FDealType.RentLongterm,
        offerType: FOfferType.Residential,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, prepay: undefined, zalog: undefined };
      },
      element: <RentTerms />,
    },

    // Комиссия
    comission: {
      showCondition: {
        dealType: FDealType.RentLongterm,
        offerType:
          (FOfferType.Any & ~FOfferType.Land & ~FOfferType.Coworking) | (FOfferType.Commercial & ~FOfferType.Coworking),
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, commission_type: undefined, cl: undefined };
      },
      element: <Comission />,
    },

    // Площадь дома
    houseArea: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Suburban & ~FOfferType.Land,
        custom: isGeoAvailable(moscowMoIds, currentLocation),
      },
      clearJsonQuery: nextJsonQuery => {
        const nextOfferType = offerTypeFromJsonQuery(nextJsonQuery);

        if (nextOfferType & (FOfferType.Land | FOfferType.CommercialLand)) {
          return { ...nextJsonQuery, total_area: undefined };
        }

        return nextJsonQuery;
      },
      element: <HouseArea />,
    },

    // Площадь участка
    landArea: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Suburban | FOfferType.CommercialLand,
        custom:
          (offerType & (FOfferType.Land | FOfferType.CommercialLand)) === 0 ||
          isGeoAvailable(moscowMoIds, currentLocation),
      },
      clearJsonQuery: nextJsonQuery => {
        const nextOfferType = offerTypeFromJsonQuery(nextJsonQuery);

        if ((nextOfferType & (FOfferType.Suburban | FOfferType.CommercialLand)) === 0) {
          return { ...nextJsonQuery, site: undefined };
        }

        return nextJsonQuery;
      },
      element: <SiteArea />,
    },

    // Отопление
    heatingType: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Suburban & ~FOfferType.Land,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, heating_source_type: undefined };
      },
      element: <Heating />,
    },

    // Санузел
    suburbanBathroom: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Suburban & ~FOfferType.Land,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, wc_site: undefined };
      },
      element: <SuburbanWC />,
    },

    // Спален
    bedrooms: {
      showCondition: {
        dealType: FDealType.Sale | FDealType.RentLongterm,
        offerType: FOfferType.Suburban & ~FOfferType.Land,
      },
      clearJsonQuery: nextJsonQuery => {
        const nextDealType = dealTypeFromJsonQuery(nextJsonQuery);
        const nextOfferType = offerTypeFromJsonQuery(nextJsonQuery);

        if (nextDealType === FDealType.RentDaily && nextOfferType === FOfferType.House) {
          return nextJsonQuery;
        }

        return { ...nextJsonQuery, bedroom_total: undefined };
      },
      element: <BedroomsCount />,
    },

    // Наличие на участке
    suburbanUtilities: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Suburban,
      },
      clearJsonQuery: nextJsonQuery => {
        const nextOfferType = offerTypeFromJsonQuery(nextJsonQuery);

        if ((nextOfferType & FOfferType.Garage) !== 0) {
          return { ...nextJsonQuery, gas: undefined, electricity: undefined, has_drainage: undefined };
        }

        return {
          ...nextJsonQuery,
          gas: undefined,
          electricity: undefined,
          has_drainage: undefined,
          has_water: undefined,
        };
      },
      element: <SuburbanUtilities />,
    },

    // Статус участка
    landStatus: {
      showCondition: {
        dealType: FDealType.Sale,
        offerType: FOfferType.Suburban,
      },
      distinctJsonQuery: nextJsonQuery => {
        const nextOfferType = offerTypeFromJsonQuery(nextJsonQuery);

        let nextLandStatuses = nextJsonQuery.land_status && nextJsonQuery.land_status.value;
        if ((nextOfferType & FOfferType.Land) === 0 && nextLandStatuses) {
          nextLandStatuses = nextLandStatuses.filter(type => type !== ELandStatusType.InvestmentProject);

          if (nextLandStatuses.length === 0) {
            nextLandStatuses = undefined;
          }
        }

        return {
          ...nextJsonQuery,
          land_status: nextLandStatuses
            ? {
                type: 'terms',
                value: nextLandStatuses,
              }
            : undefined,
        };
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, land_status: undefined };
      },
      element: <LandStatus offerType={offerType} />,
    },

    // Удаленность
    mkadDistance: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Suburban,
        custom: isGeoAvailable(moscowMoIds, currentLocation),
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, from_mcad_km: undefined };
      },
      element: <FromMKAD />,
    },

    // Наличие фото
    withPhoto: {
      showCondition: {
        dealType: FDealType.Any,
        // Отображаем всегда (PM-5572), было Commercial
        offerType: FOfferType.Any & ~FOfferType.Coworking,
      },
      element: <WithPhoto />,
    },

    // Возможное назначение / Сфера деятельности (бизнес) (1/2)
    activityField: {
      showCondition: {
        dealType: FDealType.Any,
        offerType:
          FOfferType.TradeArea |
          FOfferType.FreeAppointmentObject |
          FOfferType.PublicCatering |
          FOfferType.AutoService |
          FOfferType.DomesticServices |
          FOfferType.Business |
          FOfferType.Office |
          FOfferType.Manufacture |
          FOfferType.Warehouse,
      },
      element: <SpecialtyTypes />,
    },

    // Возможное назначение (здание)
    buildingPurpose: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Building,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, building_type__building: undefined };
      },
      element: <BuildingPurpose />,
    },

    // Тип здания
    buildingType: {
      showCondition: {
        dealType: FDealType.Any,
        offerType:
          FOfferType.Office |
          FOfferType.TradeArea |
          FOfferType.Manufacture |
          FOfferType.Warehouse |
          FOfferType.FreeAppointmentObject |
          FOfferType.PublicCatering |
          FOfferType.AutoService |
          FOfferType.DomesticServices,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, building_type2: undefined };
      },
      element: <BuildingType />,
    },

    // Класс (1/2)
    buildingClass: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Office | FOfferType.TradeArea | FOfferType.Building,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, building_class_type: undefined };
      },
      element: (
        <BuildingClass
          titleComment={paramTitleHelper([FOfferType.Office, FOfferType.TradeArea, FOfferType.Building])}
        />
      ),
    },

    // Класс (2/2)
    buildingClassWarehouse: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Manufacture | FOfferType.Warehouse,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, building_class_type__warehouse: undefined };
      },
      element: (
        <WarehouseBuildingClass titleComment={paramTitleHelper([FOfferType.Manufacture, FOfferType.Warehouse])} />
      ),
    },

    // Тип помещения
    placementType: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.TradeArea,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, placement_type: undefined };
      },
      element: <PlacementTypeComponent />,
    },

    // Линия домов (1/2)
    houseLineShoppingArea: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.TradeArea,
        custom: jsonQuery.placement_type && jsonQuery.placement_type.value.includes(EPlacementType.StreetRetail),
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, building_house_line_type__shopping_area: undefined };
      },
      element: <HouseLineShoppingArea />,
    },

    // Тип недвижимости
    propertyType: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Business,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, property_type: undefined };
      },
      element: <PropertyType />,
    },

    // Недвижимость в
    estateType: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Business,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, estate_type: undefined };
      },
      element: <EstateType />,
    },

    // Линия домов (2/2)
    houseLineBuilding: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Building,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, building_house_line_type: undefined };
      },
      element: <HouseLineBuilding />,
    },

    // Вход (офис)
    accessSystem: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Office,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, enter: undefined };
      },
      element: <AccessSystem titleComment={paramTitleHelper([FOfferType.Office])} />,
    },

    // Вход (остальные)
    entrance: {
      showCondition: {
        dealType: FDealType.Any,
        offerType:
          FOfferType.TradeArea |
          FOfferType.FreeAppointmentObject |
          FOfferType.PublicCatering |
          FOfferType.AutoService |
          FOfferType.DomesticServices |
          FOfferType.Building |
          FOfferType.Business,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, input_type: undefined };
      },
      element: (
        <Entrance
          titleComment={paramTitleHelper([
            FOfferType.TradeArea,
            FOfferType.FreeAppointmentObject,
            FOfferType.PublicCatering,
            FOfferType.AutoService,
            FOfferType.DomesticServices,
            FOfferType.Building,
            FOfferType.Business,
          ])}
        />
      ),
    },

    // Тип договора
    contract: {
      showCondition: {
        dealType: FDealType.Any,
        offerType:
          FOfferType.Office |
          FOfferType.TradeArea |
          FOfferType.FreeAppointmentObject |
          FOfferType.AutoService |
          FOfferType.Warehouse |
          FOfferType.Manufacture |
          FOfferType.DomesticServices |
          FOfferType.PublicCatering,
      },
      distinctJsonQuery: nextJsonQuery => {
        const nextDealType = dealTypeFromJsonQuery(nextJsonQuery);

        let nextContractTypes =
          nextJsonQuery.contract &&
          nextJsonQuery.contract.value.filter(type => {
            if (nextDealType & FDealType.Rent) {
              return !new Array<EContractType>(EContractType.ObjSale, EContractType.PermTransfer).includes(type);
            } else if (nextDealType & FDealType.Sale) {
              return !new Array<EContractType>(EContractType.DirectRent, EContractType.SubRent).includes(type);
            }

            return false;
          });

        if (nextContractTypes && nextContractTypes.length === 0) {
          nextContractTypes = undefined;
        }

        return {
          ...nextJsonQuery,
          contract: nextContractTypes
            ? {
                type: 'terms',
                value: nextContractTypes,
              }
            : undefined,
        };
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, contract: undefined };
      },
      element: <Contract dealType={dealType} />,
    },

    // Состояние (офис)
    officeCondition: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Office,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, condition_type: undefined };
      },
      element: <OfficeCondition titleComment={paramTitleHelper([FOfferType.Office])} />,
    },

    // Состояние (2/3)
    manufactureCondition: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Manufacture | FOfferType.Warehouse,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, condition_type__industry: undefined };
      },
      element: <ManufactureCondition titleComment={paramTitleHelper([FOfferType.Manufacture, FOfferType.Warehouse])} />,
    },

    // Состояние (3/3)
    buildingCondition: {
      showCondition: {
        dealType: FDealType.Any,
        offerType:
          FOfferType.TradeArea |
          FOfferType.FreeAppointmentObject |
          FOfferType.PublicCatering |
          FOfferType.AutoService |
          FOfferType.DomesticServices |
          FOfferType.Building |
          FOfferType.Business,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, condition_type__free_appointment_object: undefined };
      },
      element: (
        <BuildingCondition
          titleComment={paramTitleHelper([
            FOfferType.TradeArea,
            FOfferType.FreeAppointmentObject,
            FOfferType.PublicCatering,
            FOfferType.AutoService,
            FOfferType.DomesticServices,
            FOfferType.Building,
            FOfferType.Business,
          ])}
        />
      ),
    },

    // Наличие мебели
    furniture: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Office | FOfferType.TradeArea | FOfferType.Building | FOfferType.Business,
      },
      clearJsonQuery: nextJsonQuery => {
        const nextDealType = dealTypeFromJsonQuery(nextJsonQuery);

        if (nextDealType & FDealType.Rent) {
          return nextJsonQuery;
        }

        return { ...nextJsonQuery, has_furniture: undefined };
      },
      element: <HasFurniture />,
    },

    // Наличие оборудования
    equipment: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Business,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, has_equipment: undefined };
      },
      element: <HasEquipment />,
    },

    // Тип
    garageKind: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Garage,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, garage_type: undefined };
      },
      element: <Garage />,
    },

    // Тип гаража
    garageType: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Garage,
        custom:
          jsonQuery.garage_type &&
          jsonQuery.garage_type.value.length === 1 &&
          jsonQuery.garage_type.value.includes(EGarageType.Garage),
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, garage_garage_type: undefined };
      },
      element: <GarageKindParam />,
    },

    // Тип бокса
    garageMaterial: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Garage,
        custom:
          jsonQuery.garage_type &&
          jsonQuery.garage_type.value.length === 1 &&
          jsonQuery.garage_type.value.includes(EGarageType.Box),
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, garage_material: undefined };
      },
      element: <GarageMaterialParam />,
    },

    // Статус (гаража)
    garageStatus: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Garage,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, garage_status: undefined };
      },
      element: <GarageStatusParam />,
    },

    // Технические характеристики
    garageFacilities: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Garage,
      },
      clearJsonQuery: nextJsonQuery => {
        const nextOfferType = offerTypeFromJsonQuery(nextJsonQuery);

        if (isSuburban(nextOfferType)) {
          return {
            ...nextJsonQuery,
            has_electricity: undefined,
            has_extinguishing_system: undefined,
            has_heating: undefined,
            has_light: undefined,
          };
        }

        return {
          ...nextJsonQuery,
          has_electricity: undefined,
          has_extinguishing_system: undefined,
          has_heating: undefined,
          has_light: undefined,
          has_water: undefined,
        };
      },
      element: <GarageTechincalCharacteristics />,
    },

    // Лифт
    buildingElevator: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Manufacture | FOfferType.Warehouse,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, building_lift_types_type: undefined };
      },
      element: <BuildingElevator />,
    },

    // Кран
    buildingCrane: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Manufacture | FOfferType.Warehouse,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, building_cranage_types_type: undefined };
      },
      element: <BuildingCrane />,
    },

    // Ворота
    buildingGates: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Manufacture | FOfferType.Warehouse,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, building_gates_type: undefined };
      },
      element: <BuildingGates />,
    },

    // Отопление
    buildingHeating: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Manufacture | FOfferType.Warehouse,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, building_heating_type: undefined };
      },
      element: <BuildingHeating />,
    },

    // Инфраструктура
    infrastructure: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Office | FOfferType.TradeArea | FOfferType.Manufacture | FOfferType.Warehouse,
      },
      clearJsonQuery: nextJsonQuery => {
        return {
          ...nextJsonQuery,
          infrastructure_has_car_wash: undefined,
          infrastructure_has_car_service: undefined,
          infrastructure_has_pharmacy: undefined,
          infrastructure_has_clothes_studio: undefined,
          infrastructure_has_atm: undefined,
          infrastructure_has_pool: undefined,
          infrastructure_has_buffet: undefined,
          infrastructure_has_exhibition_and_warehouse_complex: undefined,
          infrastructure_has_hotel: undefined,
          infrastructure_has_cafe: undefined,
          infrastructure_has_cinema: undefined,
          infrastructure_has_conference_room: undefined,
          infrastructure_has_medical_center: undefined,
          infrastructure_has_minimarket: undefined,
          infrastructure_has_notary_office: undefined,
          infrastructure_has_bank_department: undefined,
          infrastructure_has_park: undefined,
          infrastructure_has_restaurant: undefined,
          infrastructure_has_beauty_shop: undefined,
          infrastructure_has_warehouse: undefined,
          infrastructure_has_canteen: undefined,
          infrastructure_has_supermarket: undefined,
          infrastructure_has_shopping_area: undefined,
          infrastructure_has_fitness_centre: undefined,
          infrastructure_has_studio: undefined,
          infrastructure_has_central_reception: undefined,
        };
      },
      element: <Infrastructure />,
    },
    withNeighbors: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: neightborsAvailableOfferType,
        custom: currentLocation !== 'moscow_mo' && currentLocation !== 'spb_lo' && !!currentLocation.hasNeighbors,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, with_neighbors: undefined };
      },
      element: null,
    },
    bsСenter: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Commercial,
      },
      clearJsonQuery: nextJsonQuery => {
        return { ...nextJsonQuery, bs_center: undefined };
      },
      element: null,
    },
    geoNewobject: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Residential,
      },
      clearJsonQuery: nextJsonQuery => {
        return {
          ...nextJsonQuery,
          geo: nextJsonQuery.geo && {
            ...nextJsonQuery.geo,
            value: nextJsonQuery.geo.value.filter(g => g.type !== 'newobject'),
          },
        };
      },
      element: null,
    },
    geoNewbuildingHouse: {
      showCondition: {
        dealType: FDealType.Any,
        offerType: FOfferType.Residential,
      },
      clearJsonQuery: nextJsonQuery => {
        return {
          ...nextJsonQuery,
          geo: nextJsonQuery.geo && {
            ...nextJsonQuery.geo,
            value: nextJsonQuery.geo.value.filter(g => g.type !== 'nb_house_key'),
          },
        };
      },
      element: null,
    },
    // удобства для коворкинга
    amenities: {
      showCondition: {
        dealType: FDealType.Rent,
        offerType: FOfferType.Coworking,
      },
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        amenity: undefined,
      }),
      // tslint:disable-next-line: no-any
      element: <Amenities />,
    },
    // Дата публикации для коворкинга
    coworkingPublishPeriod: {
      showCondition: {
        dealType: FDealType.Rent,
        offerType: FOfferType.Coworking,
      },
      element: <CoworkingPublishPeriod />,
    },
    // Доступ в коворкинг
    coworkingAccess: {
      showCondition: {
        dealType: FDealType.Rent,
        offerType: FOfferType.Coworking,
      },
      clearJsonQuery: jsonQuery => ({
        ...jsonQuery,
        all_day_access: undefined,
        all_week_access: undefined,
      }),
      element: <CoworkingAccess />,
    },
  };
};
